import { Terminal, Gamepad2, Code, Zap, Cpu, Shield, Binary, Users, Rocket, Map, Palette, BookOpen, Heart } from "lucide-react";
import { FaSteam, FaDiscord, FaVk, FaTelegram } from "react-icons/fa";
import { Link } from "react-router-dom";
import MatrixRain from "@/components/MatrixRain";
import FloatingActions from "@/components/FloatingActions";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import InteractiveStats from "@/components/InteractiveStats";
import FloatingParticles from "@/components/FloatingParticles";
import HoverCard3D from "@/components/HoverCard3D";
import { studioInfo, team, games, stats, socials } from "@/data/gamedev-data";

const Index = () => {

  return (
    <div className="min-h-screen bg-background relative">
      <MatrixRain />
      <Navigation />

      {/* Hero Section */}
      <section className="relative py-32 md:py-40 overflow-hidden">
        <div className="absolute inset-0 cyber-gradient opacity-20"></div>
        <div className="absolute inset-0 hex-pattern"></div>
        
        {/* ASCII Art Background - Увеличено в 1.5 раза и opacity увеличено на 10% */}
        <div className="absolute inset-0 flex items-center justify-center opacity-15 pointer-events-none scale-150">
          <pre className="font-terminal text-xs text-primary leading-tight">
{`
   ██████╗  █████╗ ███╗   ███╗███████╗██████╗ ███████╗██╗   ██╗
  ██╔════╝ ██╔══██╗████╗ ████║██╔════╝██╔══██╗██╔════╝██║   ██║
  ██║  ███╗███████║██╔████╔██║█████╗  ██║  ██║█████╗  ██║   ██║
  ██║   ██║██╔══██║██║╚██╔╝██║██╔══╝  ██║  ██║██╔══╝  ╚██╗ ██╔╝
  ╚██████╔╝██║  ██║██║ ╚═╝ ██║███████╗██████╔╝███████╗ ╚████╔╝ 
   ╚═════╝ ╚═╝  ╚═╝╚═╝     ╚═╝╚══════╝╚═════╝ ╚══════╝  ╚═══╝  
`}
          </pre>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            {/* Terminal-style greeting */}
            <div className="font-mono text-left max-w-3xl mx-auto mb-8">
              <div className="cyber-card p-4 text-xs md:text-sm">
                <div className="flex items-center gap-2 mb-2 text-secondary">
                  <Binary className="w-4 h-4" />
                  <span>system@gamedev:~$</span>
                  <span className="cursor"></span>
                </div>
                <div className="text-primary/80">
                  <span className="text-accent">&gt;</span> Инициализация... <span className="text-primary">[OK]</span>
                  <br />
                  <span className="text-accent">&gt;</span> Загрузка творческих модулей... <span className="text-primary">[OK]</span>
                  <br />
                  <span className="text-accent">&gt;</span> Запуск студии разработки игр...
                </div>
              </div>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold font-cyber">
              <span className="neon-gradient bg-clip-text text-transparent">{studioInfo.name}</span>
            </h1>
            
            <div className="flex items-center justify-center gap-2 text-sm md:text-base font-mono">
              <span className="text-primary">[</span>
              <span className="text-accent">{studioInfo.tagline}</span>
              <span className="text-primary">]</span>
            </div>
            
            <p className="text-lg md:text-xl text-foreground/70 font-mono max-w-2xl mx-auto">
              <span className="text-accent">&gt;&gt;</span> {studioInfo.description}
              <br />
              <span className="text-secondary">&gt;&gt;</span> Не берем заказы - создаем творчество
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center pt-8">
              <Link 
                to="/games"
                className="cyber-button cyber-card px-8 py-4 terminal-text font-bold font-cyber hover:scale-105 transition-transform text-lg neon-glow-blue"
              >
                <Gamepad2 className="inline-block w-5 h-5 mr-2" />
                ИГРЫ
              </Link>
              <Link 
                to="/team"
                className="cyber-button border-2 border-secondary px-8 py-4 rounded-sm terminal-text hover:bg-secondary/10 transition-colors font-cyber text-lg neon-glow-purple"
              >
                <Users className="inline-block w-5 h-5 mr-2" />
                КОМАНДА
              </Link>
              <Link 
                to="/contacts"
                className="cyber-button border-2 border-accent px-8 py-4 rounded-sm terminal-text hover:bg-accent/10 transition-colors font-cyber text-lg neon-glow-pink"
              >
                <Shield className="inline-block w-5 h-5 mr-2" />
                СВЯЗЬ
              </Link>
            </div>
            
            {/* Interactive Stats Component */}
            <InteractiveStats />
          </div>
        </div>
      </section>

      {/* Games Section - перенесено вверх */}
      <section id="games" className="py-20 relative">
        <div className="absolute inset-0"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-cyber mb-4">
              <span className="text-accent">&lt;</span>
              <span className="terminal-text">НАШИ_ИГРЫ</span>
              <span className="text-accent">/&gt;</span>
            </h2>
            <p className="font-mono text-muted-foreground text-lg">
              <span className="text-secondary">{'// '}</span>Творческие проекты, которые мы создаем
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {games.map((game, index) => (
              <div 
                key={game.id} 
                className="cyber-card p-8 space-y-6 hover:shadow-2xl hover:shadow-accent/10 transition-all duration-300"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Gamepad2 className="w-8 h-8 text-primary neon-glow-blue" />
                      <h3 className="text-2xl md:text-3xl font-bold font-cyber terminal-text">
                        {game.title}
                      </h3>
                    </div>
                    <p className="font-mono text-base text-accent mb-3">{game.tagline}</p>
                  </div>
                  <span className={`px-3 py-1 text-xs font-mono border rounded-sm ${
                    game.status === "В разработке" ? "border-secondary text-secondary" :
                    game.status === "Завершена" ? "border-primary text-primary" :
                    "border-muted text-muted-foreground"
                  }`}>
                    {game.status}
                  </span>
                </div>

                <p className="text-foreground/80 font-mono text-lg leading-relaxed">
                  {game.description}
                </p>

                <div>
                  <p className="font-mono text-sm text-primary mb-2">{'>> '} Жанр:</p>
                  <div className="flex flex-wrap gap-2">
                    {game.genre.map((g, i) => (
                      <span key={i} className="px-3 py-1.5 text-sm font-mono border border-primary/30 text-primary/80">
                        {g}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="font-mono text-sm text-accent mb-2">{'>> '} Особенности:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {game.features.map((feature, i) => (
                      <div key={i} className="text-base font-mono text-foreground/70">
                        <span className="text-secondary">&gt;</span> {feature}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ссылки на игру */}
                {game.links && (Object.keys(game.links).length > 0) && (
                  <div className="flex flex-wrap gap-3 justify-center pt-2">
                    {game.links.steam && (
                      <a
                        href={game.links.steam}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cyber-card p-4 bg-card/90 backdrop-blur-sm neon-glow-blue hover:scale-110 transition-all duration-300 group flex items-center gap-3"
                        title="Steam Store"
                      >
                        <FaSteam className="w-6 h-6 text-primary group-hover:rotate-12 transition-transform" />
                        <span className="font-mono text-sm text-foreground/90">Steam</span>
                      </a>
                    )}
                    {game.links.discord && (
                      <a
                        href={game.links.discord}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cyber-card p-4 bg-card/90 backdrop-blur-sm neon-glow-purple hover:scale-110 transition-all duration-300 group flex items-center gap-3"
                        title="Discord сервер"
                      >
                        <FaDiscord className="w-6 h-6 text-secondary group-hover:rotate-12 transition-transform" />
                        <span className="font-mono text-sm text-foreground/90">Discord</span>
                      </a>
                    )}
                    {game.links.vk && (
                      <a
                        href={game.links.vk}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cyber-card p-4 bg-card/90 backdrop-blur-sm neon-glow-pink hover:scale-110 transition-all duration-300 group flex items-center gap-3"
                        title="VK группа"
                      >
                        <FaVk className="w-6 h-6 text-accent group-hover:rotate-12 transition-transform" />
                        <span className="font-mono text-sm text-foreground/90">VK</span>
                      </a>
                    )}
                  </div>
                )}

                <div className="flex gap-1 pt-2">
                  <div className="h-1 flex-1 bg-primary/30"></div>
                  <div className="h-1 flex-1 bg-accent/30"></div>
                  <div className="h-1 flex-1 bg-secondary/30"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section - перенесено вниз */}
      <section id="team" className="py-20 relative">
        <div className="absolute inset-0"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-cyber mb-4">
              <span className="text-primary">&lt;</span>
              <span className="terminal-text">НАША_КОМАНДА</span>
              <span className="text-primary">/&gt;</span>
            </h2>
            <p className="font-mono text-muted-foreground">
              <span className="text-accent">{'// '}</span>{stats.team} творческих разработчика
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => {
              const IconComponent = 
                member.icon === "Code" ? Code :
                member.icon === "Map" ? Map :
                member.icon === "Palette" ? Palette :
                member.icon === "BookOpen" ? BookOpen : Users;

              const colorClass = 
                member.color === "primary" ? "border-primary/50 bg-gradient-to-br from-primary/20 to-primary/5" :
                member.color === "secondary" ? "border-secondary/50 bg-gradient-to-br from-secondary/20 to-secondary/5" :
                member.color === "accent" ? "border-accent/50 bg-gradient-to-br from-accent/20 to-accent/5" :
                "border-primary/50 bg-gradient-to-br from-primary/15 to-accent/5";

              const iconColorClass =
                member.color === "primary" ? "text-primary" :
                member.color === "secondary" ? "text-secondary" :
                member.color === "accent" ? "text-accent" :
                "text-primary";

              return (
                <div 
                  key={member.id} 
                  className={`cyber-card p-8 space-y-6 group transition-all duration-300 hover:scale-105 hover:shadow-2xl ${colorClass}`}
                >
                  <div className="flex items-start gap-6">
                    <div className={`p-3 rounded-sm border ${member.color === "primary" ? "border-primary" : member.color === "secondary" ? "border-secondary" : member.color === "accent" ? "border-accent" : "border-primary"} bg-background/50 group-hover:rotate-6 transition-transform duration-300`}>
                      <IconComponent className={`w-12 h-12 ${iconColorClass} neon-glow-blue group-hover:scale-125 transition-transform`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold font-cyber terminal-text mb-2 group-hover:text-accent transition-colors">
                        {member.name}
                      </h3>
                      <p className={`text-base font-mono ${iconColorClass} mb-3 font-bold`}>
                        {member.role}
                      </p>
                    </div>
                  </div>
                  
                   <p className="text-foreground/80 font-mono text-base leading-relaxed">
                    {member.description}
                  </p>
                  
                  <div className="space-y-2">
                    <p className="font-mono text-xs text-primary/80 mb-2">
                      {'>> НАВЫКИ:'}
                    </p>
                    {member.skills.slice(0, 4).map((skill, i) => (
                      <div key={i} className="text-base font-mono text-foreground/70">
                        <span className="text-secondary">&gt;</span> {skill}
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-1 pt-2">
                    <div className="h-1 flex-1 bg-primary/30"></div>
                    <div className="h-1 flex-1 bg-accent/30"></div>
                    <div className="h-1 flex-1 bg-secondary/30"></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Section - Enhanced */}
      <section id="about" className="py-20 relative">
        <FloatingParticles />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-cyber mb-4">
              <span className="text-primary">&lt;</span>
              <span className="terminal-text">НАША_ФИЛОСОФИЯ</span>
              <span className="text-primary">/&gt;</span>
            </h2>
            <p className="font-mono text-muted-foreground">
              <span className="text-accent">{'// '}</span>Что движет нашим творчеством
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <HoverCard3D
              title="Творчество"
              description="Создаем игры как форму искусства, не берем заказы"
              icon={Heart}
              color="primary"
            />
            <HoverCard3D
              title="Инновации"
              description="Экспериментируем с механиками и визуальными стилями"
              icon={Zap}
              color="accent"
            />
            <HoverCard3D
              title="Качество"
              description="Каждая деталь создается с любовью и вниманием"
              icon={Rocket}
              color="secondary"
            />
          </div>
          
          <div className="cyber-card p-10 md:p-16 space-y-8 bg-gradient-to-br from-background via-primary/5 to-background">
            <div className="space-y-6 font-mono text-foreground/80 leading-relaxed text-base">
              <div className="cyber-card p-6 bg-background/80 border-primary/30">
                <p className="text-accent font-bold mb-3 text-lg">{'>> ТВОРЧЕСТВО_БЕЗ_ГРАНИЦ'}</p>
                <p>
                  <span className="text-primary">{'> '}</span>{studioInfo.mission}
                </p>
              </div>
              
              <div className="text-center pt-4">
                <p className="font-terminal text-primary text-xl animate-pulse">
                  {'> '} СОЗДАЕМ_ИГРЫ_С_ДУШОЙ {'_'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 relative">
        <div className="absolute inset-0"></div>
        <div className="container mx-auto px-4 max-w-3xl relative z-10 text-center space-y-8">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold font-cyber mb-6">
              <span className="text-accent">&lt;</span>
              <span className="terminal-text">КОНТАКТ</span>
              <span className="text-accent">/&gt;</span>
            </h2>
            <p className="font-mono text-muted-foreground text-lg">
              <span className="text-secondary">{'// '}</span>Хотите работать с нами или обсудить проект?
            </p>
          </div>
          
          <div className="cyber-card p-8 max-w-xl mx-auto">
            <div className="space-y-4 font-mono text-left">
              <div className="flex items-center gap-3 text-base">
                <span className="text-primary">{'>'}</span>
                <span className="text-muted-foreground">Инициализация связи...</span>
                <span className="text-primary">[OK]</span>
              </div>
              <div className="flex items-center gap-3 text-base">
                <span className="text-primary">{'>'}</span>
                <span className="text-muted-foreground">Проверка протоколов...</span>
                <span className="text-primary">[OK]</span>
              </div>
              <div className="flex items-center gap-3 text-base">
                <span className="text-primary">{'>'}</span>
                <span className="text-muted-foreground">Готово к передаче данных</span>
              </div>
              <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent my-4"></div>
              <div className="text-center">
                <a 
                  href={`mailto:${studioInfo.contact.email}`}
                  className="terminal-text text-xl hover:text-accent transition-colors neon-glow-blue inline-block"
                >
                  {studioInfo.contact.email}
                </a>
              </div>
            </div>
          </div>
          
          {/* Social Networks */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-8">
            {/* Discord студии */}
            <a
              href={socials.discord.url}
              target="_blank"
              rel="noopener noreferrer"
              className="cyber-card p-5 text-center hover:scale-105 transition-all group neon-glow-blue hover:border-primary"
              title={socials.discord.label}
            >
              <FaDiscord className="w-8 h-8 text-primary mx-auto mb-2 group-hover:rotate-12 transition-transform" />
              <div className="text-primary font-mono text-sm mb-1">{'<Discord/>'}</div>
              <div className="font-terminal text-xs text-foreground/70">{socials.discord.handle}</div>
            </a>
            
            {/* Telegram студии */}
            <a
              href={socials.telegram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="cyber-card p-5 text-center hover:scale-105 transition-all group neon-glow-pink hover:border-accent"
              title={socials.telegram.label}
            >
              <FaTelegram className="w-8 h-8 text-accent mx-auto mb-2 group-hover:rotate-12 transition-transform" />
              <div className="text-accent font-mono text-sm mb-1">{'<Telegram/>'}</div>
              <div className="font-terminal text-xs text-foreground/70">{socials.telegram.handle}</div>
            </a>
            
            {/* VK студии */}
            <a
              href={socials.vk.url}
              target="_blank"
              rel="noopener noreferrer"
              className="cyber-card p-5 text-center hover:scale-105 transition-all group neon-glow-purple hover:border-secondary"
              title={socials.vk.label}
            >
              <FaVk className="w-8 h-8 text-secondary mx-auto mb-2 group-hover:rotate-12 transition-transform" />
              <div className="text-secondary font-mono text-sm mb-1">{'<VK/>'}</div>
              <div className="font-terminal text-xs text-foreground/70">{socials.vk.handle}</div>
            </a>
            
            {/* Steam студии */}
            <a
              href={socials.steam.url}
              target="_blank"
              rel="noopener noreferrer"
              className="cyber-card p-5 text-center hover:scale-105 transition-all group neon-glow-blue hover:border-primary"
              title={socials.steam.label}
            >
              <FaSteam className="w-8 h-8 text-primary mx-auto mb-2 group-hover:rotate-12 transition-transform" />
              <div className="text-primary font-mono text-sm mb-1">{'<Steam/>'}</div>
              <div className="font-terminal text-xs text-foreground/70">{socials.steam.handle}</div>
            </a>
          </div>
        </div>
      </section>

      
      <FloatingActions />
      <Footer />
    </div>
  );
};

export default Index;